import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { commandScore, useGetAllFoodsQuery } from '@durma-soft/gros-sdk';

import { Spinner } from '@/components/shared/spinner';
import { Button } from '@/components/shared/shadcn-ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shared/shadcn-ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/shared/shadcn-ui/command';

import { cn } from '@/utils/helpers';

interface DefaultSubstituteFoodComboboxProps {
  id?: string;
  restaurantId: number;
  value: number | null;
  onChange: (foodId: number | null) => void;
}

export const DefaultSubstituteFoodCombobox = ({
  id,
  restaurantId,
  value,
  onChange,
}: DefaultSubstituteFoodComboboxProps) => {
  const { t } = useTranslation();
  const [, startTransition] = useTransition();
  const [open, setOpen] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const commandListRef = useRef<HTMLDivElement>(null);

  const { data: foods, isLoading } = useGetAllFoodsQuery(restaurantId);

  const selectedFood = useMemo(() => {
    return foods?.find((food) => food.id === value) ?? null;
  }, [foods, value]);

  const sortedData = useMemo(() => {
    return (foods ? [...foods] : [])
      .filter((element) => commandScore(element.name, searchInput) > 0)
      .sort((a, b) => {
        return (
          commandScore(b.name, searchInput) - commandScore(a.name, searchInput)
        );
      });
  }, [foods, searchInput]);

  useLayoutEffect(() => {
    if (commandListRef.current) {
      commandListRef.current.scrollTop = 0;
    }
  }, [sortedData]);

  if (isLoading) return <Spinner />;

  return (
    <div className="flex items-center gap-1">
      <Popover modal open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="flex-1 min-w-0 justify-between"
          >
            <span className="truncate">
              {selectedFood?.name ??
                t('track-location.choose-default-substitute-food')}
            </span>
            <ChevronsUpDown className="w-4 h-4 ml-2 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0">
          <Command shouldFilter={false}>
            <CommandInput
              value={searchInput}
              onValueChange={(newValue) => {
                startTransition(() => {
                  setSearchInput(newValue);
                });
              }}
              placeholder={t('common.search-placeholder')}
            />
            <CommandEmpty>{t('menu.food-not-found')}</CommandEmpty>
            <CommandGroup>
              <CommandList ref={commandListRef}>
                {sortedData.map((food) => (
                  <CommandItem
                    onSelect={() => {
                      onChange(food.id === value ? null : food.id);
                      setOpen(false);
                    }}
                    key={food.id}
                    value={`${food.name}_id:${food.id}`}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        food.id === value ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {food.name}
                  </CommandItem>
                ))}
              </CommandList>
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
      {selectedFood && (
        <Button
          type="button"
          variant="ghost"
          className="px-2"
          onClick={() => onChange(null)}
          aria-label={t('common.delete')}
        >
          <X className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
};
